import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Building2, Check, ChevronDown, Globe } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { sortClinics } from '@/lib/tenant-context';
import { Popover, PopoverContent, PopoverTrigger } from '@/v2/design-system/primitives';
import { useTenantSwitch } from './useTenantSwitch';

export function TenantSwitcher() {
  const user = useAuthStore((s) => s.user);
  const { busy, confirmation, selectClinic, showAll } = useTenantSwitch();
  const all = useLocation().pathname.replace(/\/+$/, '').toLowerCase() === '/clinicas';
  const [open, setOpen] = useState(false);
  const clinics = sortClinics(user?.clinics ?? (user?.tenant ? [user.tenant] : []));
  const active = clinics.find((clinic) => clinic.id === user?.tenantId);
  const sessionTenant = user?.tenant?.id === user?.tenantId ? user?.tenant : undefined;
  const label = all ? 'Todas as clínicas' : active?.name ?? sessionTenant?.name ?? 'Selecionar clínica';

  return (
    <>
      {confirmation}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Clínica: ${label}`}
            disabled={clinics.length === 0 || busy || !!user?.impersonation}
            title={busy ? 'Aguarde para trocar de clínica' : label}
            className="flex h-11 min-w-0 shrink items-center gap-1 rounded-so-md border border-sober-border bg-sober-surface px-3 text-so-14 font-bold text-sober-text disabled:opacity-60"
          >
            <Building2 className="size-4 shrink-0 text-sober-primary" />
            <span className="max-w-[110px] truncate sm:max-w-[180px]">{label}</span>
            <ChevronDown className="size-4 shrink-0 text-sober-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64">
          <p className="px-2.5 py-2 text-so-12 text-sober-secondary">Selecionar clínica</p>
          <div className="max-h-72 overflow-y-auto">
            {clinics.map((clinic) => (
              <button
                key={clinic.id}
                type="button"
                aria-pressed={!all && clinic.id === active?.id}
                disabled={busy || (!all && clinic.id === active?.id)}
                onClick={() => { setOpen(false); selectClinic(clinic.id); }}
                className="flex min-h-11 w-full items-center gap-3 rounded-so-sm px-2.5 py-2 text-left text-so-14 text-sober-text hover:bg-sober-app aria-pressed:font-bold aria-pressed:text-sober-primary"
              >
                <Building2 className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 break-words">{clinic.name}</span>
                {!all && clinic.id === active?.id && <Check className="size-4 shrink-0" />}
              </button>
            ))}
          </div>
          {clinics.length > 1 && (
            <button type="button" disabled={busy || all} aria-pressed={all}
              onClick={() => { setOpen(false); showAll(); }}
              className="mt-2 flex min-h-11 w-full items-center gap-3 border-t border-sober-border px-2.5 py-2 text-left text-so-14 text-sober-text aria-pressed:font-bold">
              <Globe className="size-4 shrink-0" /> Todas as clínicas
              {all && <Check className="ml-auto size-4" />}
            </button>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
